/**
 * 性能采集：Web Vitals + 路由切换耗时
 *
 * 只在超阈值时告警，达标值一律不报 —— 监控群里不需要「一切正常」的噪音。
 * 全部用 PerformanceObserver 原生实现，不引入 web-vitals 包。
 */
import { nextTick } from 'vue';
import type { Router } from 'vue-router';
import { MONITOR_CONFIG } from '@/monitor/config';
import { track } from '@/monitor/core/aggregator';

/** 累积的指标值，visibilitychange → hidden 时统一结算 */
const metrics = { fcp: 0, lcp: 0, cls: 0, inp: 0, ttfb: 0 };
/** 防止重复结算 */
let settled = false;

/**
 * 注册一个 PerformanceObserver。
 * 有意例外（CLAUDE.md「非必要不加 try/catch」）：老浏览器不支持某些 entryType 时
 * observe 会直接抛错，必须逐个包住，避免一个指标不支持导致其余指标全部失效。
 */
const observe = (
  type: string,
  handler: (entries: PerformanceEntry[]) => void,
  options: Record<string, unknown> = {},
): void => {
  try {
    const po = new PerformanceObserver((list) => handler(list.getEntries()));
    po.observe({ type, buffered: true, ...options } as PerformanceObserverInit);
  } catch {
    /** 浏览器不支持该 entryType，跳过该指标 */
  }
};

/**
 * 指标超阈值时上报。
 * 注意 message 必须保持稳定（不带实测值），实测值放 extra —— 否则每次数值不同
 * 会算出不同指纹，去重失效。
 */
const reportMetric = (name: string, value: number, threshold: number, isRatio = false): void => {
  if (value <= threshold) return;
  track({
    type: 'performance',
    level: 'warn',
    title: '性能指标超标',
    message: `${name} 超过阈值`,
    source: name,
    extra: {
      实测: isRatio ? value.toFixed(3) : `${Math.round(value)}ms`,
      阈值: isRatio ? String(threshold) : `${threshold}ms`,
      TTFB: `${Math.round(metrics.ttfb)}ms`,
    },
    timestamp: Date.now(),
  });
};

/** 页面进入后台时结算首屏类指标，只结算一次 */
const settle = (): void => {
  if (settled) return;
  settled = true;
  const { THRESHOLD } = MONITOR_CONFIG;
  reportMetric('FCP', metrics.fcp, THRESHOLD.FCP);
  reportMetric('LCP', metrics.lcp, THRESHOLD.LCP);
  reportMetric('CLS', metrics.cls, THRESHOLD.CLS, true);
  reportMetric('INP(近似)', metrics.inp, THRESHOLD.INP);
};

export const setupPerformanceCollector = (router: Router): void => {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  metrics.ttfb = nav?.responseStart ?? 0;

  observe('paint', (entries) => {
    const fcp = entries.find((entry) => entry.name === 'first-contentful-paint');
    if (fcp) metrics.fcp = fcp.startTime;
  });

  observe('largest-contentful-paint', (entries) => {
    /** LCP 会多次上报，最后一条才是最终值 */
    const last = entries[entries.length - 1];
    if (last) metrics.lcp = last.startTime;
  });

  observe('layout-shift', (entries) => {
    entries.forEach((entry) => {
      const shift = entry as PerformanceEntry & { value: number; hadRecentInput: boolean };
      /** 用户主动交互引发的位移不计入 CLS */
      if (!shift.hadRecentInput) metrics.cls += shift.value;
    });
  });

  /**
   * INP 近似：官方口径是所有交互延迟的 98 分位，实现成本高。
   * 这里取最大交互延迟代替，量级判断够用，消息中已标注「近似」。
   */
  observe(
    'event',
    (entries) => {
      entries.forEach((entry) => {
        if (entry.duration > metrics.inp) metrics.inp = entry.duration;
      });
    },
    { durationThreshold: 40 },
  );

  /** 用 visibilitychange 而非 unload：后者在移动端和 bfcache 下不可靠 */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') settle();
  });

  /** 路由切换耗时：beforeEach 打点，afterEach + nextTick 结算 */
  let routeStart = 0;
  let routeFrom = '';
  router.beforeEach((_to, from) => {
    routeStart = performance.now();
    routeFrom = from.fullPath;
  });
  router.afterEach((to) => {
    const start = routeStart;
    const from = routeFrom;
    nextTick(() => {
      const duration = performance.now() - start;
      if (duration <= MONITOR_CONFIG.THRESHOLD.ROUTE_CHANGE) return;
      track({
        type: 'performance',
        level: 'warn',
        title: '路由切换缓慢',
        message: `切换到 ${to.path} 耗时超阈值`,
        source: to.path,
        extra: {
          来源: from || '(直接进入)',
          实测: `${Math.round(duration)}ms`,
          阈值: `${MONITOR_CONFIG.THRESHOLD.ROUTE_CHANGE}ms`,
        },
        timestamp: Date.now(),
      });
    });
  });
};
